import { hockeyFormatter, stringFormatter } from '@nhl/formatter-utilities';

import { Award, AwardRecipients } from '../../../types/AwardTypes';
import awardConstants, { awardStatuses } from '../../../utils/awardConstants';

import AWARDS from './constants';
import { useAwardsQuery, useBaseAwardsQuery } from './index';

export const addAwardSlug = (award) => ({
    ...award,
    slug: encodeURI(stringFormatter.toSlug(award.name)),
});

const groupRecordsBySeasonStatus = (records) => {
    const dataMap = {};
    records.forEach((record) => {
        if (!dataMap[record.seasonId]) {
            dataMap[record.seasonId] = {};
        }

        const { status } = record;
        const seasonMap = dataMap[record.seasonId];
        if (!seasonMap[status]) {
            seasonMap[status] = [];
        }
        seasonMap[status].push(record);
    });
    return dataMap;
};

const getAwardsBySeason = (recordSeasonMap) => {
    const awards: AwardRecipients[] = [];
    Object.keys(recordSeasonMap)
        .sort()
        .reverse()
        .forEach((seasonId) => {
            const record = recordSeasonMap[seasonId];
            const winnerRecord = record?.[awardStatuses.WINNER]?.[0];
            awards.push({
                seasonId: parseInt(seasonId),
                imageUrl: winnerRecord?.imageUrl,
                secondaryImageUrl: winnerRecord?.playerImageUrl,
                summary: winnerRecord?.summary,
                ...record,
            });
        });
    return awards;
};

const getAllAwards = (recordSeasonMap) => {
    const awards: AwardRecipients[] = [];
    Object.keys(recordSeasonMap)
        .sort()
        .reverse()
        .forEach((seasonId) => {
            const record = recordSeasonMap[seasonId];
            record[awardStatuses.WINNER].forEach((winnerRecord) => {
                awards.push({
                    seasonId: parseInt(seasonId),
                    imageUrl: winnerRecord?.imageUrl,
                    secondaryImageUrl: '',
                    summary: winnerRecord?.summary,
                    [awardStatuses.WINNER]: [winnerRecord],
                });
            });
        });
    return awards;
};

const formatAwardTitle = (award, selectedWinner) => {
    const awardType = award.trophyCategory.trophyRecipientCategory;
    const endYear = hockeyFormatter.seasonEndYearFormatter(selectedWinner.seasonId);
    const winnerText = `${endYear} ${award.name} ${award.slug === AWARDS.STANLEY_CUP ? 'Champions' : 'Winner'}`;
    switch (awardType) {
        case awardConstants.TEAM:
            return selectedWinner.team && `${selectedWinner.team.fullName} - ${winnerText}`;
        case awardConstants.PLAYER:
            const player = selectedWinner?.player;
            return player?.firstName && `${player?.firstName} ${player?.lastName} - ${winnerText}`;
        case awardConstants.HEAD_COACH:
            return selectedWinner?.coach?.fullName && `${selectedWinner.coach.fullName} - ${winnerText}`;
        case awardConstants.OTHER:
            return selectedWinner.fullName && `${selectedWinner.fullName} - ${winnerText}`;
        default:
            return '';
    }
};

const getAwardsBySlug = (awardSlug) => {
    const baseAwardsResponse = useBaseAwardsQuery({});
    const { data: baseAwards = [] } = baseAwardsResponse;
    const baseAward = baseAwards.find((award) => award.slug === awardSlug) as Award;

    const awardsResponse = useAwardsQuery(baseAward);
    const { data: awards = [] } = awardsResponse;
    return {
        awards,
        baseAward,
        baseAwards,
        error: baseAwardsResponse.error || awardsResponse.error,
        isError: baseAwardsResponse.isError || awardsResponse.isError,
        isFetching: baseAwardsResponse.isFetching || awardsResponse.isFetching,
    };
};

export const mergeCaptainData = (data, playoffCaptains) => {
    const captainMap = {};
    playoffCaptains.forEach((captain) => {
        const key = `${captain.seasonId}-${captain.teamId}`;
        if (!captainMap[key]) {
            captainMap[key] = [];
        }
        captainMap[key].push(captain);
    });

    data.forEach((trophy) => {
        const key = `${trophy.seasonId}-${trophy.team.id}`;
        if (captainMap[key]) {
            trophy.captains = captainMap[key];
        }
    });
    return data;
};

export default {
    addAwardSlug,
    groupRecordsBySeasonStatus,
    getAwardsBySeason,
    getAllAwards,
    formatAwardTitle,
    getAwardsBySlug,
    mergeCaptainData,
};
